import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {
  getSchoolReportOpinions,
  getSchoolReportScoresForAttributionAsArray,
  getSchoolReportScoresForAttributionByStudent,
  getSchoolReportTemplates,
  getSchoolReportTypeEntities,
  getSchoolReportTypes,
  getSchoolReportTypesToClassesAssignment,
  getStudentEndOfYearDecisionConsultationRights,
  selectedProfile,
  selectSchool,
  StoreState
} from '@redux/reducers';
import {Observable} from 'rxjs';
import {SchoolReportTemplate, SchoolReportType} from '@model/school-report-type';
import {SchoolReportTypeToClassAssignment} from '@model/school-report-type-to-class-assignment';
import {map, switchMap, take, tap} from 'rxjs/operators';
import {
  schoolReportForStudentReset,
  schoolReportOpinionSaved,
  schoolReportOpinionsLoaded,
  schoolReportScoresForAttributionLoaded,
  schoolReportScoresForAttributionReset,
  schoolReportScoresForAttributionUpdated,
  schoolReportTemplatesLoaded,
  schoolReportTypeAutomaticPeriodCalculationSaved,
  schoolReportTypePeriodCreated,
  schoolReportTypePeriodDeleted,
  schoolReportTypePeriodUpdated,
  schoolReportTypeTextualScoreCreated,
  schoolReportTypeTextualScoreDeleted,
  studentEndOfYearDecisionConsultationRightLoaded,
  studentEndOfYearDecisionConsultationRightSaved
} from '@redux/actions/school-report.action';
import {School} from '@model/school';
import {SchoolReportsService} from '@services/school-reports.service';
import {Class} from '@model/class';
import {SchoolReportPeriod} from '@model/school-report-period';
import {SchoolReportTextualScore} from '@model/school-report-textual-score';
import {AutomaticPeriodCalculation} from '@model/automatic-period-calculation';
import {Profile} from '@model/profile';
import {AttributionService} from '@services/attribution.service';
import {SchoolReportScoreForAttribution} from '@model/school-report-score-for-attribution';
import {SchoolReportScore} from '@model/school-report-score';
import {MatSnackBar} from '@angular/material/snack-bar';
import {StudentLoadedAction} from '@redux/actions/students.action';
import {StudentService} from '@services/student.service';
import {StudentDetail} from '@model/student-detail';
import {SchoolReportForStudent} from '@model/school-report-for-student';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {StudentView} from '@model/student-view';
import {YearlyClassInformation} from '@model/yearly-class-information';
import {SchoolReportStudentOpinion} from '@model/school-report-student-opinion';
import {SchoolReportOpinion} from '@model/school-report-opinion';
import {StaffMember} from '@model/staffMember';
import {StudentInAttribution} from '@model/student-in-attribution';
import {saveAs} from 'file-saver';
import {Competence} from '@model/competence';
import {documentGenerationRequestStarted, documentGenerationRequestStopped} from '@redux/actions/ui.action';
import {Section} from '@model/section';
import {Lesson} from '@model/lesson';
import {EndOfYearDecision} from '@model/end-of-year-decision';
import {lessonsAction} from '@redux/actions/attributions.action';
import {LessonService} from '@services/lesson.service';
import {StudentEndOfYearDecisionConsultationRight} from '@model/student-end-of-year-decision-consultation-right';
import {EvaluationView} from '@model/evaluation';
import {PedagogicalFollowUpComment, PedagogicalFollowUpCommentsGroupByCat} from '@model/pedagogical-follow-up-comment';
import {PedagogicalFollowUpCategory} from '@model/pedagogical-follow-up-category';
import {PredefinedTextContentMap} from '@model/predefined-text-content';
import {SchoolSetting} from "@model/school-setting";
import {AttributionSummary} from "@model/attribution-summary";

@Injectable()
export class SchoolReportFacadeService {
  school$: Observable<School>;
  yearlyClassesInformationsEntities$: Observable<{ [classId: number]: YearlyClassInformation }>;
  profile$: Observable<Profile>;
  schoolReportTypes$: Observable<SchoolReportType[]>;
  schoolReportTypeEntities$: Observable<{ [id: number]: SchoolReportType }>;
  schoolReportToClassesAssignments$: Observable<{ [id: number]: SchoolReportTypeToClassAssignment }>;
  classes$: Observable<Class[]>;
  schoolReportScoresForAttributionAsArray$: Observable<SchoolReportScoreForAttribution[]>;
  schoolReportScoresForAttributionByStudents$: Observable<{ [studentId: number]: SchoolReportScoreForAttribution }>;
  schoolReportForStudent$: Observable<SchoolReportForStudent>;
  schoolReportTypesByAttributionId$: Observable<{ [attributionId: number]: SchoolReportType }>;
  schoolReportTypeByClassId$: Observable<{ [classId: number]: SchoolReportType }>;
  studentsByClass$: Observable<StudentView[]>;
  selectedStudent$: Observable<StudentDetail>;
  schoolReportOpinionsByStudent$: Observable<{ [studentId: number]: SchoolReportStudentOpinion }>;
  attributionsActivesForTeacher$: Observable<AttributionSummary[]>;
  staffMembers$: Observable<StaffMember[]>;
  studentsByAttribution$: Observable<{ [attributionId: number]: StudentInAttribution[] }>;
  schoolReportTemplates$: Observable<SchoolReportTemplate[]>;
  isAdmin$: Observable<boolean>;
  competences$: Observable<Competence[]>;
  documentGenerationRequestOngoing$: Observable<boolean>;
  sections$: Observable<Section[]>;
  lessons$: Observable<Lesson[]>;
  activeSchoolYear$: Observable<number>;
  studentEndOfYearDecisions$: Observable<EndOfYearDecision[]>;
  studentEndOfYearDecisionConsultationRights$: Observable<StudentEndOfYearDecisionConsultationRight[]>;
  studentEvaluations$: Observable<EvaluationView[]>;
  pedagogicalFollowUpComments$: Observable<PedagogicalFollowUpComment[]>;
  pedagogicalFollowUpCommentsGroupByCat$: Observable<PedagogicalFollowUpCommentsGroupByCat[]>;
  pedagogicalFollowUpCategories$: Observable<PedagogicalFollowUpCategory[]>;
  predefinedTextContentsMap$: Observable<PredefinedTextContentMap>;
  schoolSettings$: Observable<SchoolSetting[]>;

  constructor(private _store: Store<StoreState>,
              private _sharedFacade: SharedFacadeService,
              private _schoolReportsService: SchoolReportsService,
              private _attributionService: AttributionService,
              private _studentService: StudentService,
              private _lessonService: LessonService,
              private _snackBar: MatSnackBar) {
    this.school$ = this._store.pipe(selectSchool);
    this.yearlyClassesInformationsEntities$ = this._sharedFacade.yearlyClassesInformationsEntities$;
    this.profile$ = this._store.pipe(selectedProfile);
    this.classes$ = this.school$.pipe(
      map(school => school.classes)
    );
    this.schoolReportTypes$ = this._store.pipe(
      select(getSchoolReportTypes)
    );
    this.schoolReportTypeEntities$ = this._store.pipe(
      select(getSchoolReportTypeEntities)
    );
    this.schoolReportToClassesAssignments$ = this._store.pipe(
      select(getSchoolReportTypesToClassesAssignment)
    );
    this.schoolReportScoresForAttributionAsArray$ = this._store.pipe(
      select(getSchoolReportScoresForAttributionAsArray)
    );
    this.schoolReportScoresForAttributionByStudents$ = this._store.pipe(
      select(getSchoolReportScoresForAttributionByStudent)
    );
    this.schoolReportForStudent$ = this._sharedFacade.schoolReportForStudent$;
    this.schoolReportTypesByAttributionId$ = this._sharedFacade.schoolReportTypesByAttributionId$;
    this.schoolReportTypeByClassId$ = this._sharedFacade.schoolReportTypesByClassId$;
    this.studentsByClass$ = this._store.pipe(
      select(state => state.students.studentSearchResult.content)
    );
    this.selectedStudent$ = this._sharedFacade.selectedStudent$;
    this.schoolReportOpinionsByStudent$ = this._store.pipe(
      select(getSchoolReportOpinions)
    );
    this.attributionsActivesForTeacher$ = this._sharedFacade.attributionsActivesForTeacher$;
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.studentsByAttribution$ = this._sharedFacade.studentsByAttribution$;
    this.schoolReportTemplates$ = this._store.pipe(
      select(getSchoolReportTemplates)
    );
    this.isAdmin$ = this._sharedFacade.isAdminStaffMember$;
    this.competences$ = this._sharedFacade.competences$;
    this.documentGenerationRequestOngoing$ = this._sharedFacade.documentGenerationRequestOngoing$;
    this.sections$ = this._sharedFacade.sections$;
    this.lessons$ = this._sharedFacade.lessons$;
    this.activeSchoolYear$ =  this._sharedFacade.activeSchoolYear$;
    this.studentEndOfYearDecisions$ = this._sharedFacade.studentEndOfYearDecisions$;
    this.studentEndOfYearDecisionConsultationRights$ = this._store.pipe(
      select(getStudentEndOfYearDecisionConsultationRights)
    );
    this.studentEvaluations$ = this._sharedFacade.studentEvaluations$;
    this.pedagogicalFollowUpComments$ = this._sharedFacade.pedagogicalFollowUpComments$;
    this.pedagogicalFollowUpCommentsGroupByCat$ = this._sharedFacade.pedagogicalFollowUpCommentsGroupByCat$;
    this.pedagogicalFollowUpCategories$ = this._sharedFacade.pedagogicalFollowUpCategories$;
    this.predefinedTextContentsMap$ = this._sharedFacade.predefinedTextContentsMap$;
    this.schoolSettings$ = this._sharedFacade.schoolSettings$;
  }

  loadSchoolReportTypes() {
    this._sharedFacade.loadSchoolReportTypes();
  }

  loadYearlyClassesInformations() {
    this._sharedFacade.loadYearlyClassesInformations();
  }

  saveSchoolReportType(schoolReportType: SchoolReportType) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.saveSchoolReportType(school.id, schoolReportType))
    ).subscribe(() => this.loadSchoolReportTypes());
  }

  loadSchoolReportTypeToClassAssignments() {
    this._sharedFacade.loadSchoolReportTypeToClassAssignments();
  }

  saveSchoolReportTypeToClassAssignment(schoolReportTypeToClassAssignment: SchoolReportTypeToClassAssignment) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.saveSchoolReportTypeToClassAssignment(school.id, schoolReportTypeToClassAssignment))
    ).subscribe({
      next: () => this.loadSchoolReportTypeToClassAssignments(),
      error: err => {
        this.loadSchoolReportTypeToClassAssignments();
        throw err;
      }
    })
  }

  createSchoolReportTypePeriod(schoolReportTypeId: number, period: SchoolReportPeriod) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.createSchoolReportTypePeriod(school.id, schoolReportTypeId, period))
    ).subscribe(schoolReportType => this._store.dispatch(schoolReportTypePeriodCreated({
      schoolReportType: {
        id: schoolReportType.id,
        changes: {
          periods: schoolReportType.periods
        }
      }
    })));
  }

  updateSchoolReportTypePeriod(schoolReportTypeId: number, period: SchoolReportPeriod) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.updateSchoolReportTypePeriod(school.id, schoolReportTypeId, period))
    ).subscribe(() => this._store.dispatch(schoolReportTypePeriodUpdated({
      schoolReportTypeId: schoolReportTypeId,
      schoolReportPeriodCode: period.code,
      period: period
    })));
  }

  deleteSchoolReportTypePeriod(schoolReportTypeId: number, code: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.deleteSchoolReportTypePeriod(school.id, schoolReportTypeId, code))
    ).subscribe(schoolReportType => this._store.dispatch(schoolReportTypePeriodDeleted({
      schoolReportType: {
        id: schoolReportType.id,
        changes: {
          periods: schoolReportType.periods
        }
      }
    })));
  }

  createTextualScore(schoolReportTypeId: number, textualScore: SchoolReportTextualScore) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.createTextualScore(school.id, schoolReportTypeId, textualScore))
    ).subscribe(schoolReportType => this._store.dispatch(schoolReportTypeTextualScoreCreated({
      schoolReportType: {
        id: schoolReportType.id,
        changes: {
          textualScores: schoolReportType.textualScores
        }
      }
    })));
  }

  deleteTextualScore(schoolReportTypeId: number, textualScoreCode: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.deleteTextualScore(school.id, schoolReportTypeId, textualScoreCode))
    ).subscribe(schoolReportType => this._store.dispatch(schoolReportTypeTextualScoreDeleted({
      schoolReportType: {
        id: schoolReportType.id,
        changes: {
          textualScores: schoolReportType.textualScores
        }
      }
    })));
  }

  saveAutomaticPeriodCalculation(schoolReportTypeId: number, automaticPeriodCalculation: AutomaticPeriodCalculation) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.saveAutomaticPeriodCalculation(school.id, schoolReportTypeId, automaticPeriodCalculation))
    ).subscribe(schoolReportType => this._store.dispatch(schoolReportTypeAutomaticPeriodCalculationSaved({
      schoolReportType: {
        id: schoolReportType.id,
        changes: {
          automaticPeriodCalculations: schoolReportType.automaticPeriodCalculations
        }
      }
    })));
  }

  saveAutomaticLessonCalculation(schoolReportTypeId: number, automaticPeriodCalculation: AutomaticPeriodCalculation) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.saveAutomaticLessonCalculation(school.id, schoolReportTypeId, automaticPeriodCalculation))
    ).subscribe(schoolReportType => this._store.dispatch(schoolReportTypeAutomaticPeriodCalculationSaved({
      schoolReportType: {
        id: schoolReportType.id,
        changes: {
          automaticPeriodCalculationsByLessons: schoolReportType.automaticPeriodCalculationsByLessons
        }
      }
    })));
  }

  loadAttributionActivesForConnectedTeacher() {
    this._sharedFacade.loadAttributionsActivesForConnectedTeacher();
  }

  loadAttributionsActivesForTeacher(teacherId: number) {
    this._sharedFacade.loadAttributionsActivesForTeacher(teacherId);
  }

  loadSchoolReportScoresByAttribution(attributionId: number, periodCode?: string) {
    return this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.loadSchoolReportScoresByAttribution(school.id, attributionId, periodCode))
    ).subscribe(res => this._store.dispatch(schoolReportScoresForAttributionLoaded({schoolReportScoreForAttribution: res})));
  }

  saveSchoolReportScore(studentId: number, schoolReportScore: SchoolReportScore) {
    return this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.saveSchoolReportScore(school.id, school.activeSchoolYear, studentId, schoolReportScore)),
      tap(() => this._snackBar.open('Note enregistrée', null, {duration: 2000}))
    ).subscribe(updatedScores => this._store.dispatch(schoolReportScoresForAttributionUpdated({
      schoolReportScoresForAttributionUpdate: {
        id: studentId,
        changes: {
          scores: updatedScores
        }
      }
    })));
  }

  loadStudentsByClass(classId: number) {
    this._sharedFacade.loadStudentsByClass(classId);
  }

  selectStudent(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.getStudent(school.id, studentId))
    ).subscribe(student => this._store.dispatch(new StudentLoadedAction(student)));
  }

  resetSelectedStudent() {
    this._sharedFacade.resetSelectedStudent();
  }

  resetStudentSearch() {
    this._sharedFacade.resetStudentSearchResult();
  }

  loadSchoolReportForStudent(studentId: number) {
    this._sharedFacade.loadSchoolReportForStudent(studentId);
  }

  resetSchoolReportForStudent() {
    this._store.dispatch(schoolReportForStudentReset());
  }

  loadSchoolReportOpinions(periodCode: string, classId: number): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.loadSchoolReportOpinionsForClass(school.id, periodCode, classId))
    ).subscribe(schoolReportOpinions =>
      this._store.dispatch(schoolReportOpinionsLoaded({schoolReportOpinions: schoolReportOpinions})));
  }

  saveSchoolReportOpinion(studentId: number, opinion: SchoolReportOpinion) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.saveSchoolReportOpinions(school.id, school.activeSchoolYear, studentId, opinion))
    ).subscribe(() =>
      this._store.dispatch(schoolReportOpinionSaved({studentId: studentId, text: opinion.text})));
  }

  saveSchoolReportOpinionAndReloadSchoolReportForStudent(studentId: number, opinion: SchoolReportOpinion) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.saveSchoolReportOpinions(school.id, school.activeSchoolYear, studentId, opinion))
    ).subscribe(() => {
      this.loadSchoolReportForStudent(studentId);
    });
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  loadSchoolReportOpinionsForAttribution(periodCode: string, attributionId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.loadSchoolReportOpinionsForAttribution(school.id, periodCode, attributionId))
    ).subscribe(schoolReportOpinions =>
      this._store.dispatch(schoolReportOpinionsLoaded({schoolReportOpinions: schoolReportOpinions})));
  }

  loadStudentsByAttribution(attributionId: number) {
    this._sharedFacade.loadStudentsByAttribution(attributionId);
  }

  /**
   * Deprecated. Remove this method once the async way is ok in prod.
   */
  downloadSchoolReportPDF(clazz: Class, period: SchoolReportPeriod, showSchoolReport: boolean, showCompetencesReport: boolean, teacherRemarksAtTheEnd: boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.downloadSchoolReportPDF(school.id, school.activeSchoolYear, clazz, period, showSchoolReport, showCompetencesReport, teacherRemarksAtTheEnd))
    ).subscribe(file => {
      saveAs(file, `bulletins_${clazz.name}_${period.name}.pdf`);
    });
  }

  downloadSchoolReportPDF2(clazz: Class, period: SchoolReportPeriod, showSchoolReport: boolean, showCompetencesReport: boolean, teacherRemarksAtTheEnd: boolean) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.createSchoolReportPdfGenerationRequest(school.id, school.activeSchoolYear, clazz, period, showSchoolReport, showCompetencesReport, teacherRemarksAtTheEnd))
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }

  generateSchoolReportSnapshotsForClass(clazz: Class, period: SchoolReportPeriod, showSchoolReport: boolean, showCompetencesReport: boolean, teacherRemarksAtTheEnd: boolean) {
    this.school$.pipe(
      take(1),
      tap(() => this._snackBar.open("PDF's en cours de génération", null, {duration: 3000})),
      switchMap(school =>
        this._schoolReportsService.generateSchoolReportSnapshotsForClass(school.id, school.activeSchoolYear, clazz, period, showSchoolReport, showCompetencesReport, teacherRemarksAtTheEnd))
    ).subscribe(() => {});
  }

  loadSchoolSettings(): void {
    this._sharedFacade.loadSchoolSettings();
  }

  saveSchoolSetting(setting: SchoolSetting): void {
    this._sharedFacade.saveSchoolSetting(setting);
  }

  loadSchoolReportTemplates() {
    this._schoolReportsService.loadSchoolReportTemplates()
      .subscribe(templates => this._store.dispatch(schoolReportTemplatesLoaded({templates: templates})));
  }

  loadCompetences(attributionId: number) {
    this._sharedFacade.loadCompetences(attributionId);
  }

  loadCompetenceScoresFromStudentsInAttribution(attributionId: number, competenceId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.loadCompetenceScoresFromStudentsInAttribution(school.id, attributionId, competenceId))
    ).subscribe(res =>
      this._store.dispatch(schoolReportScoresForAttributionLoaded({schoolReportScoreForAttribution: res})));
  }

  resetCompetenceScoresFromStudentsInAttribution() {
    this._store.dispatch(schoolReportScoresForAttributionReset());
  }

  loadLessons() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._lessonService.loadLessons(school.id, school.activeSchoolYear))
    ).subscribe(lessons => this._store.dispatch(lessonsAction.setLessons({lessons: lessons})));
  }

  saveStudentEndOfYearDecision(studentId: number, studentEndOfYearDecisionPartial: EndOfYearDecision) {
    this._sharedFacade.saveStudentEndOfYearDecision(studentId, studentEndOfYearDecisionPartial);
  }

  loadStudentEndOfYearDecisionForStudent(studentId: number) {
    this._sharedFacade.loadStudentEndOfYearDecisionForStudent(studentId);
  }

  resetStudentEndOfYearDecisions() {
    this._sharedFacade.resetStudentEndOfYearDecisions();
  }

  loadStudentEndOfYearDecisionConsultationRights() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.loadStudentEndOfYearDecisionConsultationRight(school.id, school.activeSchoolYear))
    ).subscribe(consultationRights => this._store.dispatch(studentEndOfYearDecisionConsultationRightLoaded({ consultationRights })));
  }

  saveStudentEndOfYearDecisionConsultationRight(consultationRight: StudentEndOfYearDecisionConsultationRight) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.saveStudentEndOfYearDecisionConsultationRight(school.id, school.activeSchoolYear, consultationRight))
    ).subscribe(_ => this._store.dispatch(studentEndOfYearDecisionConsultationRightSaved({ consultationRight })));
  }

  downloadSchoolReportGlobalViews(clazz: Class, period: SchoolReportPeriod) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.createSchoolReportGlobalViewsPdfGenerationRequest(school.id, school.activeSchoolYear, clazz, period))
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }

  downloadSchoolReportScoresCSV(clazz: Class, period: SchoolReportPeriod): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.downloadSchoolReportScoresCSV(school.id, clazz.id, period.code))
    ).subscribe(_file => saveAs(_file.content, _file.filename));
  }

  loadStudentEvaluationsForSelectedStudent(attributionId: number, periodCode: string) {
    this.selectedStudent$.pipe(
      take(1)
    ).subscribe(student => {
      this._sharedFacade.loadStudentEvaluationsForStudent(student.id, attributionId, periodCode);
    })
  }

  loadPedagogicalFollowUpComments(studentId: number) {
    this._sharedFacade.loadPedagogicalFollowUpComments(studentId);
  }

  loadPedagogicalFollowUpCategories() {
    this._sharedFacade.loadPedagogicalFollowUpCategories();
  }

  savePedagogicalFollowUpComment(ilpComment: PedagogicalFollowUpComment) {
    this._sharedFacade.savePedagogicalFollowUpComment(ilpComment);
  }

  deletePedagogicalFollowUpComment(studentId: number, commentId: number) {
    this._sharedFacade.deletePedagogicalFollowUpComment(studentId, commentId);
  }

  loadPredefinedTextContents() {
    this._sharedFacade.loadPredefinedTextContents();
  }
}
